<script setup lang="ts">
import {computed} from "vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {FrequentationYear, FrequentationYearWeek} from "@/core/interfaces/Frequentation";
import {colorForLevel, describeYearWeek, textForLevel} from "@/agenda/lib/frequentation";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";

/**
 * The last 52 weeks as a checkerboard, one row per month, one cell per week
 * coloured by its load against the year's other weeks. This is the view that
 * shows the seasons: the August lull, the flu campaign, a resort's summer.
 */
const props = defineProps<{
    year: FrequentationYear;
}>();

interface WeekCell {
    week: FrequentationYearWeek;
    number: string;
    startLabel: string;
    description: {title: string; lines: string[]};
    /** Background and ink of the cell; absent while the week is still being sold. */
    style?: {backgroundColor: string; color: string};
}

interface MonthRow {
    key: string;
    label: string;
    weeks: WeekCell[];
}

/**
 * Weeks grouped under the month their Monday falls in, oldest first, each
 * described once here rather than at every render.
 */
const months = computed<MonthRow[]>(() => {
    const rows = new Map<string, MonthRow>();

    props.year.weeks.forEach((week) => {
        const monday = parseAgendaDate(week.start);
        const key = monday.format('YYYY-MM');
        const row = rows.get(key) ?? {key, label: monday.format('MMMM YYYY'), weeks: []};

        row.weeks.push({
            week,
            number: week.week.split('-W')[1] ?? '',
            startLabel: monday.format('D MMMM'),
            description: describeYearWeek(week, props.year.median_minutes),
            style: week.partial
                ? undefined
                : {backgroundColor: colorForLevel(week.level), color: textForLevel(week.level)}
        });
        rows.set(key, row);
    });

    return [...rows.values()];
});
</script>

<template>
  <TooltipProvider>
    <div class="flex flex-col gap-1.5">
      <div
        v-for="month in months"
        :key="month.key"
        class="grid items-center gap-2"
        style="grid-template-columns: 7.5rem 1fr"
      >
        <p class="truncate text-xs font-medium text-gray-600 first-letter:uppercase">
          {{ month.label }}
        </p>
        <div class="flex gap-1.5">
          <Tooltip v-for="cell in month.weeks" :key="cell.week.week" :delay-duration="150">
            <TooltipTrigger as-child>
              <!--
                A week still being sold has no level worth a colour: its total is
                low because it is not over, not because it was quiet.
              -->
              <div
                class="flex h-8 flex-1 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums transition-opacity hover:opacity-80"
                :class="cell.week.partial ? 'border border-dashed border-gray-300 text-gray-400' : ''"
                :style="cell.style"
                role="img"
                :aria-label="`Semaine ${cell.number} : ${cell.description.title}`"
              >
                {{ cell.number }}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" class="max-w-xs text-xs">
              <p class="font-semibold">
                Semaine du {{ cell.startLabel }} · {{ cell.description.title }}
              </p>
              <p v-for="line in cell.description.lines" :key="line">
                {{ line }}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>
